/**
 * Quarter-hour times for the composer's date picker.
 *
 * A posting time is a human decision — "Tuesday at ten" — not a measurement, and a control that
 * offers 1,440 minutes a day asks for a precision nobody means. `datetime-local` takes a `step` in
 * SECONDS, so 900 is what makes the minute field move :00 → :15 → :30 → :45.
 *
 * `step` alone is not enough: it governs how the control MOVES, not where it starts. An empty field
 * still opens empty, and the value it lands on when someone starts typing is whatever the browser
 * decides. Hence the default below.
 */

/** 15 minutes, in seconds — the `step` a datetime-local input wants. */
export const QUARTER_HOUR_STEP_SECONDS = 15 * 60;

const QUARTER_MS = 15 * 60 * 1000;

const pad = (n: number) => String(n).padStart(2, '0');

/** A Date as the `YYYY-MM-DDTHH:mm` a datetime-local input reads, in LOCAL time. */
export function toDateTimeLocal(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * The next :00/:15/:30/:45 at or after `from`, as a datetime-local value.
 *
 * Rounds UP, never down: this seeds a field for scheduling something, and the nearest quarter hour
 * is in the past half the time. Seconds and milliseconds are dropped, so 10:00:01 gives 10:15 —
 * a time that has technically started is not a time you can still schedule into.
 */
export function nextQuarterHour(from: Date): string {
  const floor = new Date(from);
  floor.setSeconds(0, 0);
  const ms = floor.getTime();
  // Already exactly on a quarter (and on the second) — that one is still available.
  const next = ms === from.getTime() && ms % QUARTER_MS === 0 ? ms : Math.ceil((ms + 1) / QUARTER_MS) * QUARTER_MS;
  return toDateTimeLocal(new Date(next));
}

/** Whether a datetime-local value sits on a quarter hour. */
export function isOnQuarterHour(value: string): boolean {
  const minute = Number(value.slice(14, 16));
  return Number.isFinite(minute) && minute % 15 === 0;
}

/**
 * Whether a datetime-local value is already in the past.
 *
 * Parsed as LOCAL time, which is what the input means — `new Date('2026-09-23T10:00')` is local,
 * while appending a Z would make it UTC and be wrong by the offset.
 */
export function isInPast(value: string, now: Date = new Date()): boolean {
  if (!value) return false;
  const at = new Date(value).getTime();
  return Number.isFinite(at) && at < now.getTime();
}
